/* global BigInt */
import { calculationWindows, sourceIds } from '../calculations/calculationModel';
import { periodContaining } from '../calculations/periods';
import { extractSample } from '../calculations/powerBalanceDelegate';
import { floorSnapshotBoundary } from '../calculations/alignmentDelegate';

export const commitSequence = value => {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) throw new Error('Invalid energy journal cursor.');
  return BigInt(value);
};
const unwrap = result => {
  if (!result?.success || result.data?.status !== 'success') throw new Error(result?.message || 'Energy records could not be loaded.');
  return result.data;
};

// A failed or capped page never becomes a complete accounting dataset.
export async function collectEnergyPages(request, params, options = {}) {
  let cursor; const seen = new Set(); const rows = [];
  let envelope;
  const maxRecords = options.maxRecords || 1000000;
  for (;;) {
    if (options.signal?.aborted) throw new DOMException('Scope changed', 'AbortError');
    const response = await request({ ...params, ...(cursor ? { cursor } : {}) }, { signal: options.signal, ...(options.timeout ? { timeout: options.timeout } : {}) });
    if (!response?.success) return response;
    envelope = response.data;
    if (envelope?.status !== 'success' || !Array.isArray(envelope.data)) return { success: false, message: 'Energy history is incomplete. Retry the selected period.' };
    const mapped = envelope.data.map(options.mapRow || (row => row));
    if (options.onPage) await options.onPage(mapped); else rows.push(...mapped);
    if (!options.onPage && rows.length > maxRecords) return { success: false, code: 'BROWSER_DATA_BUDGET_EXCEEDED', message: 'This period exceeds the browser data budget. Select a shorter period.' };
    cursor = envelope.metadata?.nextCursor;
    if (!cursor) return { success: true, data: { ...envelope, data: rows, metadata: { ...envelope.metadata, nextCursor: null } } };
    if (seen.has(cursor)) return { success: false, message: 'Energy history could not be completed. Retry the selected period.' };
    seen.add(cursor);
  }
}

export function validateReplay(data, scope, confirmed) {
  if (data.rebootstrapRequired || data.complete !== true) throw new Error('REBOOTSTRAP_REQUIRED');
  if (commitSequence(data.fromCommitSeq) !== commitSequence(confirmed)) throw new Error('REPLAY_CURSOR_MISMATCH');
  if (data.customerId !== scope.customerId || !data.scope || data.scope.customerId !== scope.customerId
    || (data.scope.siteId || null) !== (scope.siteId || null) || (data.scope.fleetId || null) !== null) throw new Error('REPLAY_SCOPE_MISMATCH');
  const through = commitSequence(data.throughCommitSeq);
  if (through < commitSequence(confirmed) || !Array.isArray(data.events) || data.events.length > 500) throw new Error('REBOOTSTRAP_REQUIRED');
  let previous = commitSequence(confirmed);
  const unique = [];
  for (const event of data.events) {
    const seq = commitSequence(event.commitSeq);
    if (event.customerId !== scope.customerId || (event.siteId && event.siteId !== scope.siteId) || seq > through || seq < previous || seq <= commitSequence(confirmed)) throw new Error('REPLAY_SCOPE_OR_ORDER_MISMATCH');
    if (seq === previous) continue;
    previous = seq; unique.push(event);
  }
  return unique;
}

export class EnergySync {
  constructor({ api, scope, token, calculator, range = 'day', socketFactory = url => new WebSocket(url),
    url = process.env.REACT_APP_WEBSOCKET_URL || 'wss://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/ws',
    onUpdate = () => {}, onStatus = () => {}, now = Date.now }) {
    Object.assign(this, { api, scope, token, calculator, range, socketFactory, url, onUpdate, onStatus, now });
    this.controller = new AbortController(); this.confirmedCommitSeq = null; this.highestReceivedCommitSeq = '0';
    this.frames = new Map(); this.closed = false; this.inFlight = null; this.catchupWanted = false;
    this.lastFrameAt = this.now(); this.reconnectAttempts = 0;
  }

  params(extra = {}) { return { customerId: this.scope.customerId, siteId: this.scope.siteId, ...extra }; }
  requestOptions() { return { signal: this.controller.signal }; }

  async start() {
    await this.connect();
    if (this.closed) return;
    this.startMonitor();
    await this.run(true);
  }

  startMonitor() {
    if (this.timer || this.closed) return;
    this.timer = setInterval(() => {
      if (this.now() - this.lastFrameAt >= 15000 && this.now() - (this.lastSilenceProbe || 0) >= 15000) {
        this.lastSilenceProbe = this.now(); this.schedule(true);
      }
      // Recalculate financial time and age badges without a telemetry poll.
      if (!this.inFlight && this.confirmedCommitSeq != null) this.runClock();
    }, 1000);
  }

  connect() {
    return new Promise((resolve, reject) => {
      const socket = this.socketFactory(this.url); this.socket = socket;
      let authenticated = false;
      const timeout = setTimeout(() => { reject(new Error('Energy authentication timed out.')); socket.close(); }, 5000);
      socket.onopen = () => socket.send(JSON.stringify({ action: 'authenticate', token: this.token,
        customerId: this.scope.customerId, siteId: this.scope.siteId }));
      socket.onmessage = ({ data }) => {
        let frame;
        try { frame = JSON.parse(data); } catch { return; }
        if (this.closed || (frame.customerId && frame.customerId !== this.scope.customerId)) return;
        this.lastFrameAt = this.now();
        if (frame.type === 'authenticated') {
          authenticated = true; clearTimeout(timeout); this.reconnectAttempts = 0;
          this.onStatus({ loading: this.confirmedCommitSeq == null, errors: [] }); resolve(); return;
        }
        if (frame.type === 'energy_commit') {
          if (frame.siteId && frame.siteId !== this.scope.siteId) return;
          try {
            const seq = commitSequence(frame.commitSeq);
            if (seq > commitSequence(this.highestReceivedCommitSeq)) this.highestReceivedCommitSeq = frame.commitSeq;
            if (this.confirmedCommitSeq == null || seq > commitSequence(this.confirmedCommitSeq)) {
              // Keep bounded payload hints; journal replay remains authoritative.
              if (this.frames.size < 500) this.frames.set(frame.commitSeq, frame);
              this.schedule();
            }
          } catch { this.schedule(true); }
        }
        if (frame.type === 'heartbeat') {
          try {
            if (this.confirmedCommitSeq != null && commitSequence(frame.latestCommitSeq) > commitSequence(this.confirmedCommitSeq)) {
              if (commitSequence(frame.latestCommitSeq) > commitSequence(this.highestReceivedCommitSeq)) this.highestReceivedCommitSeq = frame.latestCommitSeq;
              this.schedule();
            }
          } catch { this.schedule(true); }
        }
        if (frame.type === 'resync_required') this.schedule(true);
      };
      socket.onerror = () => { if (!authenticated) { clearTimeout(timeout); reject(new Error('Energy connection could not be opened.')); } };
      socket.onclose = (event) => {
        clearTimeout(timeout);
        if (!authenticated) reject(new Error('Energy connection closed before authentication.'));
        if (this.closed) return;
        if ([4401, 4403].includes(event.code)) { this.authFailed = true; clearInterval(this.timer); this.onStatus({ loading: false, refreshing: false, errors: [{ source: 'session', message: event.code === 4401 ? 'Sign in again to reconnect energy data.' : 'This energy scope is not available to your account.' }] }); return; }
        this.onStatus({ errors: [{ source: 'connection', message: 'Reconnecting energy data…' }] });
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = setTimeout(() => this.connect().then(() => {
          this.startMonitor();
          if (this.confirmedCommitSeq == null) return this.run(true);
          this.schedule(true); return undefined;
        }).catch(error => this.report(error)), Math.min(1000 * 2 ** this.reconnectAttempts++, 30000));
      };
    });
  }

  schedule(force = false) {
    if (this.closed || this.authFailed) return;
    this.catchupWanted = this.catchupWanted || force;
    if (this.confirmedCommitSeq == null || this.inFlight || this.scheduled) return;
    this.scheduled = setTimeout(() => { this.scheduled = null; this.run(false).catch(error => this.report(error)); }, 25);
  }

  async bootstrapData() {
    const params = this.params(this.powerCache || {});
    let response = unwrap(await this.api.getBootstrap(params, this.requestOptions()));
    if (this.closed) return response.data;
    // Inventory uses receipt/calendar dates while demo acquisitions use the
    // scenario clock. Ask the atomic API for configuration at the source clock
    // rather than allowing newer inventory to select a future cost profile.
    const times = (response.data.latest?.rows || []).map(extractSample)
      .filter(sample => ['site', 'bess', 'load', 'pcc', 'canopy'].includes(sample.role))
      .map(sample => Date.parse(sample.sampleTime)).filter(Number.isFinite);
    if (times.length && Date.parse(response.data.asOf) > Math.max(...times) + 10000) {
      response = unwrap(await this.api.getBootstrap({ ...params, asOf: floorSnapshotBoundary(new Date(Math.max(...times)).toISOString()) }, this.requestOptions()));
    }
    return response.data;
  }

  async bootstrap() {
    const data = await this.bootstrapData();
    if (this.closed) return;
    commitSequence(data.watermarkCommitSeq);
    if (data.customerId !== this.scope.customerId || data.siteId !== this.scope.siteId) throw new Error('BOOTSTRAP_SCOPE_MISMATCH');
    const windows = calculationWindows(data, this.range, this.now());
    // Publish today's accounting before loading older billing/lease evidence.
    // Both phases use the same atomic watermark; missing older coverage stays
    // explicitly partial in the first model.
    this.needsHistoryBootstrap = true;
    if (!this.accountingReady && data.latest?.rows?.length && Date.parse(windows.fetch.start) < Date.parse(windows.today.start)) {
      await this.loadAccounting(data, { ...windows, fetch: { ...windows.fetch, start: windows.today.start } }, false);
      if (this.closed) return;
    }
    await this.loadAccounting(data, windows, data.historyCacheReusable === true && !this.partialHistory);
    this.partialHistory = false;
    this.needsHistoryBootstrap = false;
    this.accountingReady = true;
  }

  async loadAccounting(data, windows, reuseHistory) {
    const throughCommitSeq = data.watermarkCommitSeq;
    const bounds = this.params({ ...windows.fetch, throughCommitSeq, limit: 1000 });
    const stream = await this.calculator.call('beginPowerStream', windows, data.siteConfiguration, reuseHistory);
    const witnessBounds = [...new Set([windows.selected.start, windows.selected.end, windows.today.start, windows.today.end,
      windows.billing.start, windows.billing.end, windows.lease.start, windows.lease.end])].join(',');
    // Fetch only counter series consumed by the delegates. Daily registers and
    // unrelated BESS registers otherwise multiply the witness query's work.
    const series = [
      ['production', 'solar.inverter.lifetime'], ['battery', 'bess.charged.lifetime'],
      ['battery', 'bess.discharged.lifetime'], ['pcc', 'pcc.imported.lifetime'],
      ['pcc', 'pcc.exported.lifetime'], ['servedLoad', 'load.served.lifetime'],
    ].flatMap(([role, metric]) => sourceIds(data.siteConfiguration, role).map(deviceId => ({ deviceId, metric })));
    const historicalOptions = { ...this.requestOptions(), timeout: 30000 };
    const gcd = (a, b) => b ? gcd(b, a % b) : a;
    const counterParams = { ...bounds, counterWitnesses: true,
      witnessStepSeconds: gcd(data.siteConfiguration?.demandIntervalMinutes || 15, 60) * 60,
      witnessAnchor: periodContaining(windows.fetch.start, 'day', windows.timezone).periodStart, witnessBounds };
    const collectCounters = async () => {
      if (!series.length) return collectEnergyPages(this.api.getCounterObservations, counterParams, historicalOptions);
      const all = [];
      for (let offset = 0; offset < series.length; offset += 2) {
        const pages = await Promise.all(series.slice(offset, offset + 2).map(selector =>
          collectEnergyPages(this.api.getCounterObservations, { ...counterParams, ...selector }, historicalOptions)));
        for (const page of pages) { if (!page?.success) return page; all.push(...unwrap(page).data); }
        if (all.length > 1000000) throw new Error('This period exceeds the browser data budget. Select a shorter period.');
      }
      return { success: true, data: { status: 'success', data: all } };
    };
    const [history, counters, events, equipment] = await Promise.all([
      collectEnergyPages(this.api.getHistory, { ...bounds, start: new Date(Date.parse(bounds.start) - 15000).toISOString(), powerOnly: true, ...(stream?.reused ? { afterCommitSeq: stream.afterCommitSeq } : {}) }, { ...historicalOptions,
        onPage: rows => this.calculator.call('appendPowerPage', rows) }),
      collectCounters(),
      collectEnergyPages(this.api.getCounterEvents, bounds, this.requestOptions()),
      collectEnergyPages(this.api.getEquipment, this.params({ throughCommitSeq, limit: 1000 }), this.requestOptions()),
    ]);
    const datasets = { history: unwrap(history).data, counterObservations: unwrap(counters).data,
      counterEvents: unwrap(events).data, equipment: unwrap(equipment).data, canopyInventory: this.scope.canopyInventory || [] };
    if (this.closed) return;
    const calculated = await this.calculator.call('bootstrap', data, datasets, this.scope, this.range, this.now());
    if (this.closed) return;
    this.confirmedCommitSeq = throughCommitSeq;
    if (commitSequence(this.highestReceivedCommitSeq) < commitSequence(throughCommitSeq)) this.highestReceivedCommitSeq = throughCommitSeq;
    this.frames.forEach((_frame, seq) => { if (commitSequence(seq) <= commitSequence(throughCommitSeq)) this.frames.delete(seq); });
    this.powerCache = calculated.powerCache;
    this.partialHistory = this.needsHistoryBootstrap;
    this.onUpdate(calculated, this.cursors());
  }

  cursors() { return { confirmedCommitSeq: this.confirmedCommitSeq, highestReceivedCommitSeq: this.highestReceivedCommitSeq }; }

  async run(rebootstrap = false) {
    if (this.closed || this.authFailed) return;
    if (this.inFlight) { this.catchupWanted = true; this.rebootstrapWanted ||= rebootstrap; return this.inFlight; }
    this.onStatus({ loading: this.confirmedCommitSeq == null, refreshing: this.confirmedCommitSeq != null });
    this.inFlight = (async () => {
      this.catchupWanted = false;
      if (rebootstrap || this.confirmedCommitSeq == null || this.needsHistoryBootstrap) { await this.bootstrap(); return; }
      const from = this.confirmedCommitSeq;
      const envelope = unwrap(await this.api.getCommits(this.params({ sinceCommitSeq: from }), this.requestOptions()));
      if (this.closed) return;
      let events;
      try { events = validateReplay(envelope.data, this.scope, from); }
      catch (error) { if (error.message === 'REBOOTSTRAP_REQUIRED') { await this.bootstrap(); return; } throw error; }
      // These changes invalidate atomic period/configuration evidence. Rebuild at
      // a fresh atomic bootstrap watermark instead of mixing independent reads.
      if (events.some(event => ['equipment_updated', 'historical_observation_inserted', 'counter_discontinuity_recorded', 'site_configuration_changed', 'cost_profile_changed'].includes(event.eventKind))) {
        await this.bootstrap(); return;
      }
      if (this.confirmedCommitSeq !== from) throw new Error('REPLAY_CURSOR_MISMATCH');
      let calculated;
      try { calculated = await this.calculator.call('replay', events, envelope.data.throughCommitSeq, this.range, this.now()); }
      catch (error) { if (error.message === 'REBOOTSTRAP_REQUIRED') { await this.bootstrap(); return; } throw error; }
      if (this.closed) return;
      this.confirmedCommitSeq = envelope.data.throughCommitSeq;
      this.frames.forEach((_frame, seq) => { if (commitSequence(seq) <= commitSequence(this.confirmedCommitSeq)) this.frames.delete(seq); });
      this.powerCache = calculated.powerCache;
      this.onUpdate(calculated, this.cursors());
    })();
    try { await this.inFlight; this.failed = false; if (!this.closed) this.onStatus({ loading: false, refreshing: false, errors: [] }); }
    catch (error) { this.report(error); throw error; }
    finally {
      this.inFlight = null;
      if (!this.closed && (this.failed || (this.confirmedCommitSeq != null && (this.catchupWanted || commitSequence(this.highestReceivedCommitSeq) > commitSequence(this.confirmedCommitSeq))))) {
        clearTimeout(this.retryTimer);
        const force = this.rebootstrapWanted; this.rebootstrapWanted = false;
        this.retryTimer = setTimeout(() => this.run(force).catch(() => {}), this.failed ? 5000 : 0);
      }
    }
  }

  async runClock() {
    if (this.clockInFlight || this.closed) return;
    this.clockInFlight = true;
    const before = this.confirmedCommitSeq;
    try {
      const calculated = await this.calculator.call('clock', this.range, this.now());
      if (!this.closed && before === this.confirmedCommitSeq) this.onUpdate(calculated, this.cursors());
    } catch (error) { this.report(error); }
    finally { this.clockInFlight = false; }
  }

  report(error) {
    if (this.closed || error.name === 'AbortError') return;
    this.failed = true;
    const message = /^[A-Z][A-Z_]+$/.test(error.message) ? 'Energy data could not be verified. Use Refresh to retry.' : error.message;
    this.onStatus({ loading: false, refreshing: false, errors: [{ source: 'energy', message: message || 'Energy data could not be synchronized.' }] });
  }

  close() {
    this.closed = true; this.controller.abort(); this.socket?.close(); this.calculator.close();
    clearInterval(this.timer); clearTimeout(this.scheduled); clearTimeout(this.retryTimer); clearTimeout(this.reconnectTimer);
  }
}
